import React, { ReactNode } from "react";
import clsx from "clsx";
import { X } from "lucide-react";

export interface BadgeProps {
  children: ReactNode;
  variant?:
    | "default"
    | "primary"
    | "accent"
    | "info"
    | "success"
    | "warning"
    | "destructive"
    | "outline";
  style?: "solid" | "subtle" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  interactive?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

/**
 * TracQ Brand Badge-Komponente (Tailwind v4)
 * - Variants: default, primary, accent, info, success, warning, destructive, outline
 * - Styles: solid, subtle, outline, ghost
 * - Sizes: sm, md, lg
 * - Features: interactive, dismissible
 * - TracQ Brand Colors & Dark Mode
 * - Barrierefrei & konsistent
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = "default",
  style = "solid",
  size = "md",
  interactive = false,
  dismissible = false,
  onDismiss,
  className,
}) => {
  // Size styles
  const sizeStyles = {
    sm: "text-xs px-2 py-0.5 rounded",
    md: "text-sm px-3 py-1 rounded-md",
    lg: "text-base px-4 py-1.5 rounded-lg",
  };

  // Variant styles for solid
  const solidVariantStyles = {
    default: "bg-neutral-600 text-white",
    primary: "bg-tracq-deep-blue text-white",
    accent: "bg-tracq-deep-orange text-white",
    info: "bg-blue-600 text-white",
    success: "bg-green-600 text-white",
    warning: "bg-orange-600 text-white",
    destructive: "bg-red-600 text-white",
    outline: "bg-transparent border border-neutral-600 text-neutral-300",
  };

  // Variant styles for subtle
  const subtleVariantStyles = {
    default: "bg-neutral-600/20 text-neutral-300",
    primary: "bg-tracq-deep-blue/20 text-tracq-bright-blue",
    accent: "bg-tracq-deep-orange/20 text-tracq-light-orange",
    info: "bg-blue-600/20 text-blue-400",
    success: "bg-green-600/20 text-green-400",
    warning: "bg-orange-600/20 text-orange-400",
    destructive: "bg-red-600/20 text-red-400",
    outline: "bg-transparent border border-neutral-600 text-neutral-300",
  };

  // Variant styles for outline
  const outlineVariantStyles = {
    default: "bg-transparent border border-neutral-600 text-neutral-300",
    primary: "bg-transparent border border-tracq-deep-blue text-tracq-bright-blue",
    accent: "bg-transparent border border-tracq-deep-orange text-tracq-light-orange",
    info: "bg-transparent border border-blue-600 text-blue-400",
    success: "bg-transparent border border-green-600 text-green-400",
    warning: "bg-transparent border border-orange-600 text-orange-400",
    destructive: "bg-transparent border border-red-600 text-red-400",
    outline: "bg-transparent border border-neutral-600 text-neutral-300",
  };

  // Variant styles for ghost
  const ghostVariantStyles = {
    default: "bg-transparent text-neutral-300",
    primary: "bg-transparent text-tracq-bright-blue",
    accent: "bg-transparent text-tracq-light-orange",
    info: "bg-transparent text-blue-400",
    success: "bg-transparent text-green-400",
    warning: "bg-transparent text-orange-400",
    destructive: "bg-transparent text-red-400",
    outline: "bg-transparent text-neutral-300",
  };

  // Get style-specific variant styles
  const getVariantStyles = () => {
    switch (style) {
      case "solid":
        return solidVariantStyles[variant];
      case "subtle":
        return subtleVariantStyles[variant];
      case "outline":
        return outlineVariantStyles[variant];
      case "ghost":
        return ghostVariantStyles[variant];
      default:
        return solidVariantStyles[variant];
    }
  };

  // Interactive styles
  const interactiveStyles = interactive
    ? "cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-md"
    : "";

  // Dismissible styles
  const dismissibleStyles = dismissible ? "pr-1" : "";

  return (
    <span
      className={clsx(
        // Base styles
        "inline-flex items-center gap-1 font-medium",
        "transition-all duration-200 ease-in-out",

        // Size styles
        sizeStyles[size],

        // Variant & style styles
        getVariantStyles(),

        // Interactive styles
        interactiveStyles,

        // Dismissible styles
        dismissibleStyles,

        className,
      )}
    >
      {children}
      {dismissible && (
        <button
          onClick={onDismiss}
          className="ml-1 p-0.5 hover:bg-black/10 rounded-full transition-colors"
          aria-label="Remove badge"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
};
